import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode
} from 'react';
import { useAuth } from './AuthContext';

// Type definitions for time state management
export interface TimeFrameState {
  visibleTimeStart: number;
  visibleTimeEnd: number;
  selectedDate?: Date;
  zoomLevel?: number;
}

export interface DateRangePickerState {
  fromDate: Date | null;
  toDate: Date | null;
  isOpen: boolean;
}

export interface DispositionCalendarContextType {
  // Current active time frame state
  timeFrame: TimeFrameState;

  // Date range picker draft state (not yet applied)
  dateRangePicker: DateRangePickerState;

  // Actions for immediate updates (navigation, zoom, timeline interaction)
  updateTimeFrame: (updates: Partial<TimeFrameState>) => void;

  // Actions for deferred updates (date range picker)
  updateDateRangePicker: (updates: Partial<DateRangePickerState>) => void;
  applyDateRangePicker: () => void;
  cancelDateRangePicker: () => void;

  // Navigation helpers
  goToPreviousDay: () => void;
  goToNextDay: () => void;
  goToToday: () => void;

  // Zoom helpers
  zoomIn: () => void;
  zoomOut: () => void;
  setZoomLevel: (level: number) => void;
}

const DispositionCalendarContext = createContext<
  DispositionCalendarContextType | undefined
>(undefined);

interface DispositionCalendarProviderProps {
  children: ReactNode;
}

export const DispositionCalendarProvider: React.FC<
  DispositionCalendarProviderProps
> = ({ children }) => {
  const { user } = useAuth();

  // Generate unique storage key for user/company isolation
  const getStorageKey = useCallback(() => {
    if (!user?.username || !user?.companyId) return null;
    return `dispositionCalendar_${user.username}_${user.companyId}`;
  }, [user?.username, user?.companyId]);

  // Load initial state from localStorage
  const loadInitialState = useCallback((): TimeFrameState => {
    const storageKey = getStorageKey();
    if (!storageKey) {
      // Default to today if no user context
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);

      return {
        visibleTimeStart: todayStart.getTime(),
        visibleTimeEnd: todayEnd.getTime(),
        selectedDate: new Date(),
        zoomLevel: 1
      };
    }

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (
          typeof parsed === 'object' &&
          typeof parsed.visibleTimeStart === 'number' &&
          typeof parsed.visibleTimeEnd === 'number' &&
          parsed.visibleTimeStart < parsed.visibleTimeEnd
        ) {
          return {
            visibleTimeStart: parsed.visibleTimeStart,
            visibleTimeEnd: parsed.visibleTimeEnd,
            selectedDate: parsed.selectedDate
              ? new Date(parsed.selectedDate)
              : new Date(),
            zoomLevel: parsed.zoomLevel || 1
          };
        }
      }
    } catch (error) {
      console.warn('Failed to load saved time frame state:', error);
    }

    // Fallback to today
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);

    return {
      visibleTimeStart: todayStart.getTime(),
      visibleTimeEnd: todayEnd.getTime(),
      selectedDate: new Date(),
      zoomLevel: 1
    };
  }, [getStorageKey]);

  // State management
  const [timeFrame, setTimeFrame] = useState<TimeFrameState>(loadInitialState);
  const [dateRangePicker, setDateRangePicker] = useState<DateRangePickerState>({
    fromDate: null,
    toDate: null,
    isOpen: false
  });

  // Persist to localStorage whenever timeFrame changes
  useEffect(() => {
    const storageKey = getStorageKey();
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(timeFrame));
      } catch (error) {
        console.warn('Failed to save time frame state:', error);
      }
    }
  }, [timeFrame, getStorageKey]);

  // Actions for immediate updates
  const updateTimeFrame = useCallback((updates: Partial<TimeFrameState>) => {
    setTimeFrame((prev) => ({
      ...prev,
      ...updates
    }));
  }, []);

  // Actions for deferred updates (date range picker)
  const updateDateRangePicker = useCallback(
    (updates: Partial<DateRangePickerState>) => {
      setDateRangePicker((prev) => ({
        ...prev,
        ...updates
      }));
    },
    []
  );

  const applyDateRangePicker = useCallback(() => {
    if (dateRangePicker.fromDate && dateRangePicker.toDate) {
      const startTime = new Date(dateRangePicker.fromDate);
      startTime.setHours(0, 0, 0, 0);

      const endTime = new Date(dateRangePicker.toDate);
      endTime.setHours(23, 59, 59, 999);

      updateTimeFrame({
        visibleTimeStart: startTime.getTime(),
        visibleTimeEnd: endTime.getTime(),
        selectedDate: dateRangePicker.fromDate
      });
    }

    // Close the picker
    setDateRangePicker({
      fromDate: null,
      toDate: null,
      isOpen: false
    });
  }, [dateRangePicker.fromDate, dateRangePicker.toDate, updateTimeFrame]);

  const cancelDateRangePicker = useCallback(() => {
    setDateRangePicker({
      fromDate: null,
      toDate: null,
      isOpen: false
    });
  }, []);

  // Navigation helpers
  const goToPreviousDay = useCallback(() => {
    const currentStart = new Date(timeFrame.visibleTimeStart);
    const currentEnd = new Date(timeFrame.visibleTimeEnd);
    const duration = currentEnd.getTime() - currentStart.getTime();

    const newStart = new Date(currentStart.getTime() - 24 * 60 * 60 * 1000); // Go back 1 day
    const newEnd = new Date(newStart.getTime() + duration);

    updateTimeFrame({
      visibleTimeStart: newStart.getTime(),
      visibleTimeEnd: newEnd.getTime(),
      selectedDate: newStart
    });
  }, [timeFrame.visibleTimeStart, timeFrame.visibleTimeEnd, updateTimeFrame]);

  const goToNextDay = useCallback(() => {
    const currentStart = new Date(timeFrame.visibleTimeStart);
    const currentEnd = new Date(timeFrame.visibleTimeEnd);
    const duration = currentEnd.getTime() - currentStart.getTime();

    const newStart = new Date(currentStart.getTime() + 24 * 60 * 60 * 1000); // Go forward 1 day
    const newEnd = new Date(newStart.getTime() + duration);

    updateTimeFrame({
      visibleTimeStart: newStart.getTime(),
      visibleTimeEnd: newEnd.getTime(),
      selectedDate: newStart
    });
  }, [timeFrame.visibleTimeStart, timeFrame.visibleTimeEnd, updateTimeFrame]);

  const goToToday = useCallback(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);

    updateTimeFrame({
      visibleTimeStart: todayStart.getTime(),
      visibleTimeEnd: todayEnd.getTime(),
      selectedDate: new Date()
    });
  }, [updateTimeFrame]);

  // Zoom helpers
  const zoomIn = useCallback(() => {
    const currentStart = new Date(timeFrame.visibleTimeStart);
    const currentEnd = new Date(timeFrame.visibleTimeEnd);
    const center = new Date(
      (currentStart.getTime() + currentEnd.getTime()) / 2
    );
    const currentDuration = currentEnd.getTime() - currentStart.getTime();
    const newDuration = currentDuration * 0.5; // Zoom in by 50%

    const newStart = new Date(center.getTime() - newDuration / 2);
    const newEnd = new Date(center.getTime() + newDuration / 2);

    updateTimeFrame({
      visibleTimeStart: newStart.getTime(),
      visibleTimeEnd: newEnd.getTime(),
      zoomLevel: (timeFrame.zoomLevel || 1) * 2
    });
  }, [
    timeFrame.visibleTimeStart,
    timeFrame.visibleTimeEnd,
    timeFrame.zoomLevel,
    updateTimeFrame
  ]);

  const zoomOut = useCallback(() => {
    const currentStart = new Date(timeFrame.visibleTimeStart);
    const currentEnd = new Date(timeFrame.visibleTimeEnd);
    const center = new Date(
      (currentStart.getTime() + currentEnd.getTime()) / 2
    );
    const currentDuration = currentEnd.getTime() - currentStart.getTime();
    const newDuration = currentDuration * 2; // Zoom out by 100%

    const newStart = new Date(center.getTime() - newDuration / 2);
    const newEnd = new Date(center.getTime() + newDuration / 2);

    updateTimeFrame({
      visibleTimeStart: newStart.getTime(),
      visibleTimeEnd: newEnd.getTime(),
      zoomLevel: (timeFrame.zoomLevel || 1) / 2
    });
  }, [
    timeFrame.visibleTimeStart,
    timeFrame.visibleTimeEnd,
    timeFrame.zoomLevel,
    updateTimeFrame
  ]);

  const setZoomLevel = useCallback(
    (level: number) => {
      updateTimeFrame({
        zoomLevel: level
      });
    },
    [updateTimeFrame]
  );

  const contextValue: DispositionCalendarContextType = {
    timeFrame,
    dateRangePicker,
    updateTimeFrame,
    updateDateRangePicker,
    applyDateRangePicker,
    cancelDateRangePicker,
    goToPreviousDay,
    goToNextDay,
    goToToday,
    zoomIn,
    zoomOut,
    setZoomLevel
  };

  return (
    <DispositionCalendarContext.Provider value={contextValue}>
      {children}
    </DispositionCalendarContext.Provider>
  );
};

// Custom hook to use the context
export const useDispositionCalendar = (): DispositionCalendarContextType => {
  const context = useContext(DispositionCalendarContext);
  if (context === undefined) {
    throw new Error(
      'useDispositionCalendar must be used within a DispositionCalendarProvider'
    );
  }
  return context;
};
